import type { TFunction } from 'i18next';
import type {
  AcceptedBy,
  ArchiveCheck,
  ArchiveSummary,
  PlanOwner,
  ResidualDecision,
  ResidualDecisionType,
  ResidualRisk,
  ResidualRiskRequest,
  SafetyRepMissing,
  ShareKind,
  WorkEnvironmentArchive,
  WorkEnvironmentSurvey,
} from '../../api/workEnvironmentTypes';
import { WorkEnvironmentSurveyStatus } from '../../api/workEnvironmentTypes';
import { formatDay, fullDate } from '../HRDashboard/hmsFormat';
import { missingSuffix } from './safetyRepView';

/*
 * Which risks remain, what the checklist requires and whether the work can be archived is
 * decided in the API. What is here picks what the page shows, and checks the decision form
 * while it is filled in.
 */

export const RESIDUAL_DECISIONS: ResidualDecisionType[] = [
  'PLAN',
  'NEXT_SURVEY',
  'ACCEPT',
];

/** Who can own a later survey, and who can accept a risk. Without a safety representative, the API leaves them out. */
export type ResidualChoices = Pick<WorkEnvironmentArchive, 'owners' | 'acceptedBy'>;

/** Archived after the evaluation, as opposed to archived at the risk step with too few answers */
export const archivedAfterEvaluation = (
  survey: Pick<WorkEnvironmentSurvey, 'status' | 'actionPlanOpenedAt'>,
) =>
  survey.status === WorkEnvironmentSurveyStatus.ARCHIVED &&
  Boolean(survey.actionPlanOpenedAt);

export const residualProgress = (risks: ResidualRisk[]) => ({
  done: risks.filter((risk) => risk.decision).length,
  total: risks.length,
});

export const residualReasonText = (risk: ResidualRisk, t: TFunction) =>
  t(`workEnvironmentArchive.residual.reasons.${risk.reason}`, {
    before: t(
      `workEnvironmentRisk.matrix.levels.${risk.before.level}`,
    ).toLowerCase(),
  });

export const ownerLabel = (
  owner: Pick<PlanOwner, 'email' | 'role'>,
  t: TFunction,
) =>
  t(`workEnvironmentPlan.builder.ownerRole.${owner.role}`, {
    email: owner.email,
  });

/** What was decided, in one sentence, as in the list and in the documentation */
export const decisionText = (
  decision: Pick<
    ResidualDecision,
    'decision' | 'nextSurveyBy' | 'acceptedBy' | 'reason'
  > & {
    owner: string | null;
  },
  t: TFunction,
  language: string,
) =>
  t(`workEnvironmentArchive.residual.decided.${decision.decision}`, {
    date: decision.nextSurveyBy
      ? fullDate(decision.nextSurveyBy, language)
      : '',
    owner: decision.owner ?? '',
    by: decision.acceptedBy
      ? t(
          `workEnvironmentArchive.residual.acceptedByText.${decision.acceptedBy}`,
        )
      : '',
    reason: decision.reason ?? '',
  });

export type ResidualDraft = {
  decision: ResidualDecisionType | null;
  nextSurveyBy: string;
  ownerId: number | null;
  acceptedBy: AcceptedBy | null;
  reason: string;
};

export type ResidualField =
  | 'decision'
  | 'nextSurveyBy'
  | 'owner'
  | 'acceptedBy'
  | 'reason';

export const residualDraft = (
  decision: ResidualDecision | null,
): ResidualDraft => ({
  decision: decision?.decision ?? null,
  nextSurveyBy: decision?.nextSurveyBy ?? '',
  ownerId: decision?.owner?.id ?? null,
  acceptedBy: decision?.acceptedBy ?? null,
  reason: decision?.reason ?? '',
});

const FIELD_RULES: Record<
  ResidualDecisionType,
  (draft: ResidualDraft, today: string) => ResidualField[]
> = {
  PLAN: () => [],
  NEXT_SURVEY: (draft, today) => [
    ...(draft.nextSurveyBy > today ? [] : ['nextSurveyBy' as const]),
    ...(draft.ownerId ? [] : ['owner' as const]),
  ],
  ACCEPT: (draft) => [
    ...(draft.acceptedBy ? [] : ['acceptedBy' as const]),
    ...(draft.reason.trim() ? [] : ['reason' as const]),
  ],
};

/** The same rules as the API, so the form can say what is missing before it is saved */
export const draftErrors = (
  draft: ResidualDraft,
  today: string,
): ResidualField[] =>
  draft.decision ? FIELD_RULES[draft.decision](draft, today) : ['decision'];

export const toRequest = (draft: ResidualDraft): ResidualRiskRequest => ({
  decision: draft.decision ?? 'PLAN',
  ...(draft.decision === 'NEXT_SURVEY' && {
    nextSurveyBy: draft.nextSurveyBy,
    ownerId: draft.ownerId,
  }),
  ...(draft.decision === 'ACCEPT' && {
    acceptedBy: draft.acceptedBy,
    reason: draft.reason.trim(),
  }),
});

export const residualFieldId = (key: string, field: ResidualField) =>
  `residual-${key}-${field}`;

export const residualItemId = (key: string) => `residual-${key}`;

const day = (value: string | null, t: TFunction, language: string) =>
  value ? formatDay(value, t, language) : '';

/** Where the signature of the safety representative is. Without one, the deviation is what remains. */
export const signatureNote = (
  signature: ArchiveSummary['signature'],
  missing: SafetyRepMissing | null,
  t: TFunction,
  language: string,
) => {
  const noRep = missingSuffix(missing);
  return signature
    ? t(
        `workEnvironmentArchive.checklist.notes.safetyRep.${signature.status}${
          signature.status === 'DEVIATION' ? noRep : ''
        }`,
        {
          date: day(
            signature.respondedAt ?? signature.requestedAt,
            t,
            language,
          ),
          comment: signature.comment ?? '',
        },
      ).trim()
    : t(`workEnvironmentArchive.checklist.notes.safetyRep.none${noRep}`);
};

/**
 * The team leader asks until the documentation is signed, and asks again after a return.
 * Without a safety representative there is no one to ask, only the deviation to register.
 * The API says when a deviation can be registered.
 */
export const signatureActions = (
  signature: ArchiveSummary['signature'],
  { hasSafetyRep, canRegisterDeviation }: Pick<WorkEnvironmentArchive, 'hasSafetyRep' | 'canRegisterDeviation'>,
) => ({
  ask:
    hasSafetyRep &&
    (!signature ||
      signature.status === 'RETURNED' ||
      signature.status === 'DEVIATION' ||
      signature.status === 'WITHDRAWN'),
  remind: hasSafetyRep && signature?.status === 'REQUESTED',
  withdraw: signature?.status === 'REQUESTED',
  deviation: canRegisterDeviation && signature?.status !== 'DEVIATION',
});

/**
 * A deviation documents the work, but says the safety representative did not take part. Without anyone who could
 * sign, it says why instead of naming a safety representative.
 */
export const checkLabel = (
  check: WorkEnvironmentArchive['checklist'][number],
  summary: Pick<ArchiveSummary, 'signature'>,
  t: TFunction,
  missing: SafetyRepMissing | null = null,
) =>
  t(
    `workEnvironmentArchive.checklist.labels.${check.key}${
      check.key === 'safetyRep' && summary.signature?.status === 'DEVIATION'
        ? `Deviation${missingSuffix(missing)}`
        : ''
    }`,
  );

/** What each check shows, from what the API found */
const NOTES: Record<
  ArchiveCheck,
  (
    summary: ArchiveSummary,
    t: TFunction,
    language: string,
    done: boolean,
    missing: SafetyRepMissing | null,
  ) => string
> = {
  survey: (summary, t, language) =>
    t('workEnvironmentArchive.checklist.notes.survey', {
      responses: summary.responses,
      expected: summary.expected,
      date: day(summary.surveyedAt, t, language),
    }),
  assessment: (summary, t, language) =>
    summary.assessment
      ? t('workEnvironmentArchive.checklist.notes.assessment', {
          count: summary.areas,
          date: day(summary.assessment.at, t, language),
        })
      : t('workEnvironmentArchive.checklist.notes.assessmentMissing'),
  participation: (summary, t) =>
    t('workEnvironmentArchive.checklist.notes.participation', {
      proposals: summary.proposals,
      voters: summary.voters,
      expected: summary.expected,
    }),
  safetyRep: (summary, t, language, _done, missing) =>
    [
      signatureNote(summary.signature, missing, t, language),
      ...summary.deviations.map((reason) =>
        t('workEnvironmentArchive.checklist.notes.deviation', { reason }),
      ),
    ].join(' '),
  measuresPlanned: (summary, t, _language, done) =>
    done
      ? t('workEnvironmentArchive.checklist.notes.measuresPlanned', {
          count: summary.measures,
        })
      : t('workEnvironmentArchive.checklist.notes.measuresPlannedMissing'),
  measuresDone: (summary, t, _language, done) =>
    [
      t('workEnvironmentArchive.checklist.notes.measuresDone', {
        done: summary.measuresDone,
        count: summary.measures,
      }),
      ...(done
        ? []
        : [t('workEnvironmentArchive.checklist.notes.measuresLeft')]),
    ].join(' '),
  evaluated: (summary, t, language) =>
    t('workEnvironmentArchive.checklist.notes.evaluated', {
      date: day(summary.evaluatedAt, t, language),
    }),
  combined: (summary, t, language, done) =>
    done
      ? t(
          summary.assessment?.participation === 'SAFETY_REP'
            ? 'workEnvironmentArchive.checklist.notes.combinedWithSafetyRep'
            : 'workEnvironmentArchive.checklist.notes.combined',
          { date: day(summary.assessment?.at ?? null, t, language) },
        )
      : t('workEnvironmentArchive.checklist.notes.combinedMissing'),
  shared: (summary, t, language, done) =>
    done
      ? t('workEnvironmentArchive.checklist.notes.shared', {
          result: day(summary.sharedResultAt, t, language),
          plan: day(summary.sharedPlanAt, t, language),
        })
      : t('workEnvironmentArchive.checklist.notes.sharedMissing'),
  residual: () => '',
};

export const checkNote = (
  check: WorkEnvironmentArchive['checklist'][number],
  archive: Pick<WorkEnvironmentArchive, 'summary' | 'residual' | 'safetyRepMissing'>,
  t: TFunction,
  language: string,
) =>
  check.key === 'residual'
    ? t(
        'workEnvironmentArchive.checklist.notes.residual',
        residualProgress(archive.residual),
      )
    : NOTES[check.key](archive.summary, t, language, check.done, archive.safetyRepMissing);

/** What the team has not got yet, so it can be sent from the checklist */
export const missingShares = (summary: ArchiveSummary): ShareKind[] => [
  ...(summary.sharedResultAt ? [] : ['RESULT' as const]),
  ...(summary.sharedPlanAt ? [] : ['PLAN' as const]),
];

export const CHECKLIST_ID = 'archive-checklist';

/** The error summary when archiving is tried too early, each item linking to where it is fixed */
export const missingForArchive = (
  archive: WorkEnvironmentArchive,
  t: TFunction,
  language: string,
) => [
  ...archive.checklist
    .filter(
      (check) => check.required && !check.done && check.key !== 'residual',
    )
    .map((check) => ({
      key: check.key,
      href: `#${CHECKLIST_ID}`,
      text: `${checkLabel(check, archive.summary, t, archive.safetyRepMissing)}: ${checkNote(check, archive, t, language)}`,
    })),
  ...archive.residual
    .filter((risk) => !risk.decision)
    .map((risk) => ({
      key: risk.key,
      href: `#${residualItemId(risk.key)}`,
      text: t('workEnvironmentArchive.checklist.residualMissing', {
        name: risk.name,
      }),
    })),
];
